import * as React from "react";
import { Alert, Button, Card, Modal, Popconfirm, Space, Tag, Tooltip } from "antd";
import { CloseOutlined, EditOutlined, EllipsisOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Table } from "../../newcomponents/table/table";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CardContent } from "../../components/antd/card/CardContent";
import { IdentityInput } from "../../schema/types/identity/input";
import { v4 as uuidv4 } from "uuid";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  checkCanReach,
  fetchRegions,
  fetchServices,
  selectService,
  setCloudaccountLink,
} from "../../store/slices/browserServiceSlice";
import { BrowserServiceModal } from "./create/browserServiceModal";
import { slug } from "github-slugger";
import { formSet, formSetValue, formTriggerValidation, useCpFormValue } from "../gvc/testNewForm/utils";
import { store } from "../../store";
import { cloneDeep } from "lodash";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { CpArrayString } from "../gvc/testNewForm/CpArrayString";
import { CpFormItem } from "../gvc/testNewForm/CpFormItem";
import { CpInput } from "../gvc/testNewForm/CpInput";
import { CpErrorWarning } from "../gvc/testNewForm/CpErrorWarning";
import { CpLabel } from "../gvc/testNewForm/CpLabel";
import { CpDescription } from "../gvc/testNewForm/CpDescription";
import { getDefaultKindSelectState } from "../../components/antd/kindselect/KindSelect";
import { CpKindSelect } from "../gvc/testNewForm/temp/CpKindSelect";

export const IdentityCreateNetworkResourceGCP: React.FC = () => {
  const { id } = useCpFormContext();

  const isAddingItem = useCpFormValue(`nativeNetworkResources.gcpItemForm.isAddingItem`);
  const setIsAddingItem = (value: string | boolean) => {
    formSetValue(id, `nativeNetworkResources.gcpItemForm.isAddingItem`, value);
  };

  const gcpResources = useCpFormValue(`nativeNetworkResources.gcp`);
  let filteredGcpResources = gcpResources;
  filteredGcpResources = filteredGcpResources.filter((r) => {
    if (isAddingItem !== true && isAddingItem !== false) {
      return r.id !== isAddingItem;
    }
    return true;
  });
  const ipResources = useCpFormValue(`networkResources.ip`);
  const fqdnResources = useCpFormValue(`networkResources.fqdn`);
  const awsResources = useCpFormValue(`nativeNetworkResources.aws`);

  const allResourcesFiltered = [...filteredGcpResources, ...fqdnResources, ...ipResources, ...awsResources];
  const allFqdns = React.useMemo(() => allResourcesFiltered.map((r) => (r as any).fqdn).filter(Boolean), [
    allResourcesFiltered,
  ]) as string[];
  const allNames = React.useMemo(() => allResourcesFiltered.map((r) => r.name).filter(Boolean), [
    allResourcesFiltered,
  ]) as string[];

  const onDoneItemForm = async () => {
    const form = store.getState().form.instances[id];
    const values: IdentityInput = form.values as any;

    const itemForm = cloneDeep(values.nativeNetworkResources.gcpItemForm);
    let currentGcpArray = cloneDeep(values.nativeNetworkResources.gcp);
    if (typeof isAddingItem !== "boolean") {
      currentGcpArray = currentGcpArray.filter((r) => r.id !== isAddingItem);
    }

    if (!itemForm.name) {
      itemForm.name = itemForm.generatedName;
    }

    currentGcpArray.push({
      id: uuidv4(),
      fqdn: itemForm.fqdn,
      name: itemForm.name,
      ports: itemForm.ports.map((p) => p.value),
      gcpServiceConnect: {
        targetService: itemForm.gcpServiceConnect.targetService,
      },
    });

    formSetValue(id, `nativeNetworkResources.gcp`, currentGcpArray);
    formSet(id, `fieldStates.nativeNetworkResources.gcpItemForm.fqdn.simulateTouched`, false);
    formSet(id, `fieldStates.nativeNetworkResources.gcpItemForm.name.simulateTouched`, false);
    formSet(id, `fieldStates.nativeNetworkResources.gcpItemForm.gcpServiceConnect.targetService.simulateTouched`, false);
    formSet(id, `fieldStates.nativeNetworkResources.gcpItemForm.portsroot.simulateTouched`, false);
    formTriggerValidation(id);
    setIsAddingItem(false);
  };

  const onRemoveItem = async (index: number) => {
    const form = store.getState().form.instances[id];
    const values: IdentityInput = form.values as any;

    const currentGcpArray = cloneDeep(values.nativeNetworkResources.gcp);
    currentGcpArray.splice(index, 1);
    formSetValue(id, `nativeNetworkResources.gcp`, currentGcpArray);
    formTriggerValidation(id);
  };

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>GCP Service Connect Resources</CardTitle>
          <CardContent>
            {gcpResources.length > 0 ? (
              <Table<any>
                tableId="gcpResourcesEditable"
                data={gcpResources}
                hideSettings
                columns={[
                  { id: "fqdn", label: "FQDN", enableResize: true, size: 200 },
                  { id: "name", label: "Name", enableResize: true, size: 200 },
                  {
                    id: "gcpServiceConnect.targetService",
                    label: "Service Connect Name",
                    enableResize: true,
                    size: 200,
                  },
                  {
                    id: "ports",
                    label: "Ports",
                    enableResize: true,
                    size: 200,
                    cell: ({ row }) => {
                      const ports = row.original.ports || [];
                      return (
                        <div className="flex items-center">
                          {ports.map((port) => (
                            <Tag key={port}>{port}</Tag>
                          ))}
                        </div>
                      );
                    },
                  },
                  {
                    id: "action",
                    label: "",
                    enableResize: false,
                    size: 120,
                    cell: ({ row }) => {
                      return (
                        <div className="w-full flex justify-end">
                          <Space.Compact>
                            <Button
                              htmlType={`button`}
                              onClick={() => {
                                const item = gcpResources.find((r) => r.id === row.original.id);
                                if (item) {
                                  formSetValue(id, `nativeNetworkResources.gcpItemForm`, {
                                    isAddingItem: row.original.id,
                                    cloudAccount: getDefaultKindSelectState(),
                                    fqdn: item.fqdn,
                                    name: item.name,
                                    generatedName: item.name,
                                    ports: item.ports.map((p) => ({ id: uuidv4(), value: p })),
                                    gcpServiceConnect: {
                                      targetService: item.gcpServiceConnect.targetService,
                                    },
                                  });
                                }
                              }}
                              type={"text"}
                              icon={<EditOutlined />}
                            />
                            <Popconfirm title={"Confirm"} onConfirm={() => onRemoveItem(row.index)}>
                              <Button htmlType={`button`} type={"text"} icon={<CloseOutlined />} danger />
                            </Popconfirm>
                          </Space.Compact>
                        </div>
                      );
                    },
                  },
                ]}
              />
            ) : null}
            {!isAddingItem ? null : (
              <ItemFormContent
                isAddingItem={isAddingItem}
                allFqdns={allFqdns}
                allNames={allNames}
                onCancel={() => {
                  // Clear simulateTouched states
                  formSet(id, `fieldStates.nativeNetworkResources.gcpItemForm.fqdn.simulateTouched`, false);
                  formSet(id, `fieldStates.nativeNetworkResources.gcpItemForm.name.simulateTouched`, false);
                  formSet(id, `fieldStates.nativeNetworkResources.gcpItemForm.gcpServiceConnect.targetService.simulateTouched`, false);
                  formSet(id, `fieldStates.nativeNetworkResources.gcpItemForm.portsroot.simulateTouched`, false);
                  formSetValue(id, `nativeNetworkResources.gcpItemForm`, {
                    isAddingItem: false,
                    cloudAccount: getDefaultKindSelectState(),
                    fqdn: "",
                    name: "",
                    generatedName: "",
                    ports: [],
                    gcpServiceConnect: { targetService: "" },
                  });
                }}
                onDone={onDoneItemForm}
              />
            )}
            <Button
              htmlType={`button`}
              className="mt-4"
              type={"dashed"}
              icon={<PlusCircleOutlined />}
              onClick={() => {
                formSetValue(id, `nativeNetworkResources.gcpItemForm`, {
                  isAddingItem: true,
                  cloudAccount: getDefaultKindSelectState(),
                  fqdn: "",
                  name: "",
                  generatedName: "",
                  ports: [],
                  gcpServiceConnect: { targetService: "" },
                });
              }}
            >
              Add GCP Resource
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

interface Props {
  isAddingItem: boolean | string;
  allFqdns: string[];
  allNames: string[];
  onCancel: any;
  onDone: any;
}
const ItemFormContent: React.FC<Props> = ({ isAddingItem, allFqdns, allNames, onCancel, onDone }) => {
  const { id } = useCpFormContext();
  const cloudAccountLink = useCpFormValue(`nativeNetworkResources.gcpItemForm.cloudAccount.link`);
  const [isBrowsing, setIsBrowsing] = React.useState(false);

  const dispatch = useAppDispatch();
  const [isCheckingReach, setIsCheckingReach] = React.useState(false);
  const bss = useAppSelector((state) => state.browserService);
  const canReach = useAppSelector((state) => state.browserService.canReach);

  React.useEffect(() => {
    prepareService();
  }, [cloudAccountLink]);

  React.useEffect(() => {
    if (!isBrowsing) {
      return;
    }
    prepareBrowser();
  }, [isBrowsing]);

  async function prepareService() {
    setIsCheckingReach(true);
    dispatch(setCloudaccountLink(cloudAccountLink));
    await dispatch(checkCanReach()).unwrap();
    setIsCheckingReach(false);
  }

  async function prepareBrowser() {
    if (!canReach) {
      return;
    }

    await dispatch(fetchServices()).unwrap();
    const service_compute = bss.servicesSection.items.find((service) => service.href.includes("/compute"));
    if (!service_compute) {
      return;
    }
    dispatch(selectService(service_compute.href));
    await dispatch(fetchRegions()).unwrap();
  }

  const fqdn = useCpFormValue(`nativeNetworkResources.gcpItemForm.fqdn`);
  React.useEffect(() => {
    const generatedName = slug(fqdn);
    formSetValue(id, `nativeNetworkResources.gcpItemForm.generatedName`, generatedName);
  }, [fqdn]);

  const name = useCpFormValue(`nativeNetworkResources.gcpItemForm.name`);
  const generatedName = useCpFormValue(`nativeNetworkResources.gcpItemForm.generatedName`);
  const targetService = useCpFormValue(`nativeNetworkResources.gcpItemForm.gcpServiceConnect.targetService`);

  const ports = useCpFormValue(`nativeNetworkResources.gcpItemForm.ports`);

  const isFqdnUnique = !allFqdns.includes(fqdn);
  const nameToCheck = name || generatedName;
  const isNameUnique = !allNames.includes(nameToCheck);

  const isValid =
    fqdn &&
    isFqdnUnique &&
    nameToCheck &&
    isNameUnique &&
    targetService &&
    ports.length > 0 &&
    !ports.some((p) => !p.value);

  return (
    <Modal
      open={isAddingItem !== false}
      title={`${isAddingItem === true ? "Add" : "Edit"} GCP Resource`}
      okText={"Add"}
      cancelText="Cancel"
      okButtonProps={{ htmlType: "submit" }}
      onCancel={onCancel}
      onOk={() => {
        // Mark all fields as simulateTouched so error states are shown
        formSet(id, `fieldStates.nativeNetworkResources.gcpItemForm.fqdn.simulateTouched`, true);
        formSet(id, `fieldStates.nativeNetworkResources.gcpItemForm.name.simulateTouched`, true);
        formSet(id, `fieldStates.nativeNetworkResources.gcpItemForm.gcpServiceConnect.targetService.simulateTouched`, true);
        formSet(id, `fieldStates.nativeNetworkResources.gcpItemForm.portsroot.simulateTouched`, true);
        formTriggerValidation(id);
        if (!isValid) {
          return;
        }
        onDone();
      }}
      destroyOnHidden
      closable={false}
      maskClosable={false}
    >
      {isFqdnUnique ? null : <Alert className="mb-2" type={`error`} message={`All "FQDN" values must be unique`} />}
      {isNameUnique ? null : <Alert className="mb-2" type={`error`} message={`All "name" values must be unique`} />}
      <CpFormItem>
        <CpLabel name={`nativeNetworkResources.gcpItemForm.fqdn`}>FQDN</CpLabel>
        <CpInput name={`nativeNetworkResources.gcpItemForm.fqdn`} />
        <CpErrorWarning name={`nativeNetworkResources.gcpItemForm.fqdn`} />
      </CpFormItem>
      <CpFormItem>
        <CpLabel name={`nativeNetworkResources.gcpItemForm.name`}>Name</CpLabel>
        <CpInput name={`nativeNetworkResources.gcpItemForm.name`} placeholder={generatedName} />
        <CpErrorWarning name={`nativeNetworkResources.gcpItemForm.name`} />
      </CpFormItem>
      <CpFormItem>
        <CpLabel name={`nativeNetworkResources.gcpItemForm.cloudAccount`}>Cloud Account</CpLabel>
        <CpDescription>You only need to select this to utilize browser service to view your items</CpDescription>
        <CpKindSelect
          kind="cloudaccount"
          optionValue="name"
          name={`nativeNetworkResources.gcpItemForm.cloudAccount`}
          queries={[{ property: "provider", value: "gcp" }]}
        />
        <CpErrorWarning name={`nativeNetworkResources.gcpItemForm.cloudAccount`} />
      </CpFormItem>
      <CpFormItem>
        <CpLabel name={`nativeNetworkResources.gcpItemForm.gcpServiceConnect.targetService`}>Endpoint Service</CpLabel>
        <Space.Compact>
          <CpInput name={`nativeNetworkResources.gcpItemForm.gcpServiceConnect.targetService`} />
          <Tooltip
            title={
              cloudAccountLink
                ? canReach
                  ? "Browse Resources"
                  : "Selected cloud account failed to access your cloud"
                : "Select a cloud account above to browse"
            }
          >
            <Button
              icon={<EllipsisOutlined />}
              onClick={() => setIsBrowsing(true)}
              disabled={isCheckingReach || !cloudAccountLink || !canReach}
              loading={isCheckingReach}
            />
          </Tooltip>
        </Space.Compact>
        <CpErrorWarning name={`nativeNetworkResources.gcpItemForm.gcpServiceConnect.targetService`} />
      </CpFormItem>
      <CpArrayString name={`nativeNetworkResources.gcpItemForm.ports`} label={`Ports`} />
      <BrowserServiceModal
        open={isBrowsing}
        title={`Select Service Connect Target Name`}
        cloudaccountLink={cloudAccountLink}
        restrictToVPCEndpointService={true}
        dataTypeRestrictions={["/serviceAttachment"]}
        onClose={(uri) => {
          if (uri.length > 0) {
            formSetValue(id, `nativeNetworkResources.gcpItemForm.gcpServiceConnect.targetService`, uri[0]._cpln.ref);
          }
          setIsBrowsing(false);
        }}
        selectionType={"single"}
      />
    </Modal>
  );
};
